/**
 * Form builder (ADR-0010 P5c) — configure a form's fields + test submit.
 *
 * Left: pick which of the bound table's columns are included, in order,
 * each with a label override + required toggle. Right (dialog): fill the
 * form and submit through the real `POST …/submit` endpoint, which
 * validates + inserts a row.
 *
 * The field config (`config.fields`) stores column *ids*; `formFields`
 * resolves them back to live columns, skipping any that were dropped.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Send, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { coerceValue, columnOptions, fieldKindFor } from "@/components/apps/DataGrid";
import { isComputedColumn } from "@/lib/columnPayload";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import {
  appsApi,
  type AttachmentOut,
  type ColumnOut,
  type FormConfig,
  type FormField,
  type FormSection,
  type Row,
} from "@/lib/api-apps";
import { usePermissions } from "@/lib/permissions";


/** Resolve a form's saved fields to live columns paired with their field
 *  config, skipping ids of dropped columns. Pure — unit-tested. */
export function formFields(
  columns: ColumnOut[],
  config: FormConfig | undefined,
): Array<{ column: ColumnOut; field: FormField }> {
  const byId = new Map(columns.map((c) => [c.id, c]));
  return (config?.fields ?? [])
    .map((f) => {
      const column = byId.get(f.column);
      return column ? { column, field: f } : null;
    })
    .filter((x): x is { column: ColumnOut; field: FormField } => x !== null);
}

/** Is this field required? An explicit field override wins; otherwise it
 *  inherits the column's `required`. */
export function fieldRequired(column: ColumnOut, field: FormField): boolean {
  if (field.required === true) return true;
  if (field.required === false) return false;
  return column.required;
}

/** Evaluate a field's `show_when` against the current values (keyed by
 *  column name). Empty/absent → always visible. String comparison
 *  mirrors the backend `_show_when_satisfied`. Pure. */
export function isFieldVisible(
  showWhen: Record<string, unknown> | null | undefined,
  values: Record<string, string | boolean>,
): boolean {
  if (!showWhen || Object.keys(showWhen).length === 0) return true;
  return Object.entries(showWhen).every(([col, expected]) => {
    const actual = String(values[col] ?? "");
    const wants = Array.isArray(expected) ? expected : [expected];
    return wants.map((w) => String(w)).includes(actual);
  });
}

/** Widget kinds the builder offers. "" (auto) defers to the column's
 *  data type. `date`/`email`/`url` (Form pro A) are richer HTML5 inputs over
 *  a text column. */
export const FORM_WIDGETS = [
  "", "text", "textarea", "number", "checkbox", "file", "select",
  "date", "datetime", "email", "url",
] as const;
export type FormWidget = (typeof FORM_WIDGETS)[number];

/** The effective input kind for rendering a field: an explicit widget
 *  override wins, else fall back to the column's data type. Pure. */
export function effectiveWidget(
  column: ColumnOut,
  field: FormField,
): "text" | "textarea" | "number" | "boolean" | "file" | "select" | "date" | "datetime" | "email" | "url" {
  const w = (field.widget ?? "") as FormWidget;
  if (w === "textarea") return "textarea";
  if (w === "number") return "number";
  if (w === "checkbox") return "boolean";
  if (w === "file") return "file";
  if (w === "select") return "select";
  if (w === "text") return "text";
  if (w === "date" || w === "datetime" || w === "email" || w === "url") return w;
  // auto → derive from the column type.
  const k = fieldKindFor(column.data_type);
  // json + relation + member render as a plain input in the admin form-builder
  // preview (the real pickers live in the portal record/form runtime).
  if (k === "json" || k === "relation" || k === "relation_many" || k === "member") return "text";
  return k; // 'select' falls through from a select column's data type
}


/**
 * The form builder BODY — routing-agnostic (props, not `useParams`), so it
 * mounts both in the standalone `FormBuilderPage` route shell and in the
 * in-editor `AppFormsPane` (single-IDE rail area, ADR-0048). One builder, no fork.
 */
export function FormBuilder({ appId, formId, canWrite }: { appId: string; formId: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [fillOpen, setFillOpen] = React.useState(false);

  const formQ = useQuery({
    queryKey: ["apps", appId, "forms", formId],
    queryFn: () => appsApi.getForm(appId, formId),
    enabled: !!appId && !!formId,
  });
  const tableId = formQ.data?.table_id;
  const tableQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId],
    queryFn: () => appsApi.getTable(appId, tableId!),
    enabled: !!tableId,
  });

  // Local editable copy of the field list.
  const [fields, setFields] = React.useState<FormField[]>([]);
  const [submitMessage, setSubmitMessage] = React.useState("");
  // Form pro A — layout: explicit sections + the implicit default column count.
  const [sections, setSections] = React.useState<FormSection[]>([]);
  const [defaultColumns, setDefaultColumns] = React.useState<number>(1);
  React.useEffect(() => {
    if (formQ.data) {
      setFields(formQ.data.config.fields ?? []);
      setSubmitMessage(formQ.data.config.submit_message ?? "");
      setSections(formQ.data.config.sections ?? []);
      setDefaultColumns(formQ.data.config.columns === 2 ? 2 : 1);
    }
  }, [formQ.data]);

  const save = useMutation({
    mutationFn: (config: FormConfig) => appsApi.updateForm(appId, formId, { config }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "forms", formId] }),
  });

  // A stable-ish section id without Date/Math.random sprinkled through render.
  const addSection = () =>
    setSections((prev) => [
      ...prev,
      { id: `s${prev.length + 1}_${prev.reduce((n, s) => n + s.id.length, 0)}`, title: "", columns: 2 },
    ]);
  const patchSection = (id: string, patch: Partial<FormSection>) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const removeSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    // Orphan its fields back to the default section.
    setFields((prev) => prev.map((f) => (f.section === id ? { ...f, section: null } : f)));
  };
  const moveSection = (index: number, dir: -1 | 1) =>
    setSections((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });

  if (formQ.isError) {
    return (
      <Alert tone="danger" title={t("apps.loadFormFailed", "Could not load form")}>
        <span className="font-mono text-xs">{(formQ.error as Error).message}</span>
      </Alert>
    );
  }

  const userColumns = (tableQ.data?.columns ?? []).filter(
    (c) =>
      !["id", "created_at", "updated_at", "created_by", "updated_by"].includes(c.name) &&
      !isComputedColumn(c), // computed columns are read-only → not form inputs (P1.S4)
  );
  const includedIds = new Set(fields.map((f) => f.column));
  const available = userColumns.filter((c) => !includedIds.has(c.id));

  const addField = (col: ColumnOut) =>
    setFields((prev) => [...prev, { column: col.id, required: null }]);
  const removeField = (colId: string) =>
    setFields((prev) => prev.filter((f) => f.column !== colId));
  const patchField = (colId: string, patch: Partial<FormField>) =>
    setFields((prev) => prev.map((f) => (f.column === colId ? { ...f, ...patch } : f)));
  const moveField = (index: number, dir: -1 | 1) =>
    setFields((prev) => {
      const next = [...prev];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });

  const resolved = formFields(userColumns, { fields });
  const colById = new Map(userColumns.map((c) => [c.id, c]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2 border-b border-border/50 pb-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={resolved.length === 0}
          onClick={() => setFillOpen(true)}
        >
          <Send className="h-4 w-4" strokeWidth={1.5} />
          {t("apps.fillForm", "Fill / test")}
        </Button>
        {canWrite && (
          <Button
            size="sm"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                fields,
                submit_message: submitMessage || null,
                sections,
                columns: defaultColumns,
              })
            }
          >
            {t("common.save", "Save")}
          </Button>
        )}
      </div>
      <div className="space-y-6">
        {userColumns.length === 0 ? (
          <EmptyState
            icon={Send}
            title={t("apps.noColumnsForForm", "The table has no columns")}
            description={t("apps.noColumnsForFormDescription", "Add columns to the table first, then build the form.")}
          />
        ) : (
          <>
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">
                {t("apps.formFields", "Fields")}
              </h3>

              {/* Included fields — in form order, reorderable. */}
              {fields.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("apps.noFieldsYet", "No fields yet — add columns below.")}
                </p>
              ) : (
                <div className="space-y-2">
                  {fields.map((field, idx) => {
                    const col = colById.get(field.column);
                    if (!col) return null;
                    return (
                      <div key={field.column} className="rounded-lg border border-border/60 p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <button
                              type="button"
                              aria-label={t("apps.moveUp", "Move up")}
                              disabled={idx === 0}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                              onClick={() => moveField(idx, -1)}
                            >
                              <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                            <button
                              type="button"
                              aria-label={t("apps.moveDown", "Move down")}
                              disabled={idx === fields.length - 1}
                              className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                              onClick={() => moveField(idx, 1)}
                            >
                              <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                          <span className="font-medium">{col.name}</span>
                          <span className="font-mono text-[11px] text-muted-foreground">{col.data_type}</span>
                          <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                            {t("apps.required", "Required")}
                            <Switch
                              checked={fieldRequired(col, field)}
                              onCheckedChange={(v) => patchField(col.id, { required: v })}
                            />
                          </label>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t("apps.removeField", "Remove field")}
                            onClick={() => removeField(col.id)}
                          >
                            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <Input
                            value={field.label ?? ""}
                            placeholder={t("apps.labelOverride", "Label (optional)")}
                            className="h-8"
                            onChange={(e) => patchField(col.id, { label: e.target.value || null })}
                          />
                          <select
                            value={(field.widget ?? "") as string}
                            className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                            onChange={(e) => patchField(col.id, { widget: e.target.value || null })}
                          >
                            {FORM_WIDGETS.map((w) => (
                              <option key={w || "auto"} value={w}>
                                {w || t("apps.widgetAuto", "Auto")}
                              </option>
                            ))}
                          </select>
                          <Input
                            value={field.help ?? ""}
                            placeholder={t("apps.helpText", "Help text (optional)")}
                            className="h-8"
                            onChange={(e) => patchField(col.id, { help: e.target.value || null })}
                          />
                          <Input
                            value={field.default == null ? "" : String(field.default)}
                            placeholder={t("apps.defaultValue", "Default (optional)")}
                            className="h-8"
                            onChange={(e) =>
                              patchField(col.id, { default: e.target.value === "" ? null : e.target.value })
                            }
                          />
                        </div>

                        {/* Conditional visibility (show-when). v1: one condition. */}
                        {(() => {
                          const sw = field.show_when ?? null;
                          const swCol = sw ? Object.keys(sw)[0] ?? "" : "";
                          const swVal = sw && swCol ? String(sw[swCol] ?? "") : "";
                          const others = fields
                            .filter((o) => o.column !== col.id)
                            .map((o) => colById.get(o.column))
                            .filter((c): c is ColumnOut => Boolean(c));
                          return (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {t("apps.showWhen", "Show when")}
                              </span>
                              <select
                                value={swCol}
                                className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                                onChange={(e) =>
                                  patchField(col.id, {
                                    show_when: e.target.value ? { [e.target.value]: swVal } : null,
                                  })
                                }
                              >
                                <option value="">{t("apps.always", "Always")}</option>
                                {others.map((o) => (
                                  <option key={o.id} value={o.name}>
                                    {o.name}
                                  </option>
                                ))}
                              </select>
                              {swCol && (
                                <>
                                  <span className="text-xs text-muted-foreground">=</span>
                                  <Input
                                    value={swVal}
                                    placeholder={t("apps.showWhenValue", "value")}
                                    className="h-8 max-w-[12rem]"
                                    onChange={(e) =>
                                      patchField(col.id, { show_when: { [swCol]: e.target.value } })
                                    }
                                  />
                                </>
                              )}
                            </div>
                          );
                        })()}

                        {/* Form pro A — layout: which section + how wide. */}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {t("apps.section", "Section")}
                          </span>
                          <select
                            value={field.section ?? ""}
                            className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                            onChange={(e) => patchField(col.id, { section: e.target.value || null })}
                          >
                            <option value="">{t("apps.sectionDefault", "Default")}</option>
                            {sections.map((s, i) => (
                              <option key={s.id} value={s.id}>
                                {s.title?.trim() || t("apps.sectionN", `Section ${i + 1}`, { n: i + 1 })}
                              </option>
                            ))}
                          </select>
                          <span className="text-xs text-muted-foreground">
                            {t("apps.fieldWidth", "Width")}
                          </span>
                          <select
                            value={String(field.col_span === 2 ? 2 : 1)}
                            className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                            onChange={(e) =>
                              patchField(col.id, { col_span: e.target.value === "2" ? 2 : 1 })
                            }
                          >
                            <option value="1">{t("apps.widthHalf", "Half / 1 col")}</option>
                            <option value="2">{t("apps.widthFull", "Full width")}</option>
                          </select>
                        </div>

                        {/* Form pro B — per-field validation rules. */}
                        {(() => {
                          const v = field.validation ?? {};
                          const setV = (patch: Record<string, unknown>) => {
                            const next: Record<string, unknown> = { ...v, ...patch };
                            for (const k of Object.keys(next)) {
                              if (next[k] === "" || next[k] == null) delete next[k];
                            }
                            patchField(col.id, {
                              validation: Object.keys(next).length ? next : null,
                            });
                          };
                          const num = (x: unknown) => (x == null ? "" : String(x));
                          return (
                            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border/40 pt-2">
                              <span className="text-xs text-muted-foreground">
                                {t("apps.validation", "Validation")}
                              </span>
                              <Input
                                value={(v.pattern as string) ?? ""}
                                placeholder={t("apps.patternRegex", "pattern (regex)")}
                                className="h-8 max-w-[12rem]"
                                onChange={(e) => setV({ pattern: e.target.value })}
                              />
                              <Input
                                type="number"
                                value={num(v.min_length)}
                                placeholder={t("apps.minLen", "min len")}
                                className="h-8 w-[6.5rem]"
                                onChange={(e) =>
                                  setV({ min_length: e.target.value === "" ? "" : Number(e.target.value) })
                                }
                              />
                              <Input
                                type="number"
                                value={num(v.max_length)}
                                placeholder={t("apps.maxLen", "max len")}
                                className="h-8 w-[6.5rem]"
                                onChange={(e) =>
                                  setV({ max_length: e.target.value === "" ? "" : Number(e.target.value) })
                                }
                              />
                              <Input
                                type="number"
                                value={num(v.min)}
                                placeholder={t("apps.minNum", "min")}
                                className="h-8 w-[5.5rem]"
                                onChange={(e) =>
                                  setV({ min: e.target.value === "" ? "" : Number(e.target.value) })
                                }
                              />
                              <Input
                                type="number"
                                value={num(v.max)}
                                placeholder={t("apps.maxNum", "max")}
                                className="h-8 w-[5.5rem]"
                                onChange={(e) =>
                                  setV({ max: e.target.value === "" ? "" : Number(e.target.value) })
                                }
                              />
                              <Input
                                value={(v.message as string) ?? ""}
                                placeholder={t("apps.errorMessage", "error message (optional)")}
                                className="h-8 max-w-[14rem]"
                                onChange={(e) => setV({ message: e.target.value })}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Available columns to add. */}
              {available.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {available.map((col) => (
                    <Button key={col.id} variant="ghost" size="sm" onClick={() => addField(col)}>
                      <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {col.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Form pro A — layout sections. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  {t("apps.formSections", "Sections")}
                </h3>
                <Button variant="ghost" size="sm" onClick={addSection}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {t("apps.addSection", "Add section")}
                </Button>
              </div>
              {sections.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "apps.noSectionsYet",
                    "No sections — fields stack in one column. Add a section to group fields and use two columns.",
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  {/* The implicit default section's column count. */}
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border/60 p-3">
                    <span className="text-sm font-medium">{t("apps.sectionDefault", "Default")}</span>
                    <span className="text-xs text-muted-foreground">
                      {t("apps.unsectionedFields", "Unsectioned fields")}
                    </span>
                    <select
                      value={String(defaultColumns)}
                      className="ml-auto h-8 rounded-md border border-border bg-surface px-2 text-sm"
                      onChange={(e) => setDefaultColumns(e.target.value === "2" ? 2 : 1)}
                    >
                      <option value="1">{t("apps.oneColumn", "1 column")}</option>
                      <option value="2">{t("apps.twoColumns", "2 columns")}</option>
                    </select>
                  </div>
                  {sections.map((s, idx) => (
                    <div key={s.id} className="rounded-lg border border-border/60 p-3">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            aria-label={t("apps.moveUp", "Move up")}
                            disabled={idx === 0}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                            onClick={() => moveSection(idx, -1)}
                          >
                            <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                          <button
                            type="button"
                            aria-label={t("apps.moveDown", "Move down")}
                            disabled={idx === sections.length - 1}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                            onClick={() => moveSection(idx, 1)}
                          >
                            <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                        <Input
                          value={s.title ?? ""}
                          placeholder={t("apps.sectionTitle", `Section ${idx + 1} title`, { n: idx + 1 })}
                          className="h-8 max-w-[18rem]"
                          onChange={(e) => patchSection(s.id, { title: e.target.value })}
                        />
                        <select
                          value={String(s.columns === 2 ? 2 : 1)}
                          className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
                          onChange={(e) => patchSection(s.id, { columns: e.target.value === "2" ? 2 : 1 })}
                        >
                          <option value="1">{t("apps.oneColumn", "1 column")}</option>
                          <option value="2">{t("apps.twoColumns", "2 columns")}</option>
                        </select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-auto"
                          aria-label={t("apps.removeSection", "Remove section")}
                          onClick={() => removeSection(s.id)}
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                      <Input
                        value={s.description ?? ""}
                        placeholder={t("apps.sectionDescription", "Description (optional)")}
                        className="mt-2 h-8"
                        onChange={(e) => patchSection(s.id, { description: e.target.value || null })}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="submit-msg">{t("apps.submitMessage", "Submit message")}</Label>
              <Input
                id="submit-msg"
                value={submitMessage}
                placeholder={t("apps.submitMessageHint", "Shown after a successful submission")}
                onChange={(e) => setSubmitMessage(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <FillFormDialog
        appId={appId}
        formId={formId}
        fields={resolved}
        open={fillOpen}
        onOpenChange={setFillOpen}
      />
    </div>
  );
}

/**
 * Standalone ROUTE shell for one form (`/apps/:appId/forms/:formId`). The actual
 * builder body lives in the reusable `FormBuilder`, shared with the in-editor
 * `AppFormsPane` (single-IDE "Form" rail area, ADR-0048) — so there is ONE builder.
 */
export function FormBuilderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appId = "", formId = "" } = useParams<{ appId: string; formId: string }>();
  const { can } = usePermissions();
  const canWrite = can("apps", "write");

  const formQ = useQuery({
    queryKey: ["apps", appId, "forms", formId],
    queryFn: () => appsApi.getForm(appId, formId),
    enabled: !!appId && !!formId,
  });

  return (
    <PageContainer>
      <PageHeader
        title={formQ.data?.name ?? t("apps.loading", "Loading…")}
        description={t("apps.formBuilderSubtitle", "Choose fields and test the submission")}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate(`/apps/${appId}`)}>
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.backToApp", "Back to app")}
          </Button>
        }
      />
      <PageContent>
        <FormBuilder appId={appId} formId={formId} canWrite={canWrite} />
      </PageContent>
    </PageContainer>
  );
}


function FillFormDialog({
  appId,
  formId,
  fields,
  open,
  onOpenChange,
}: {
  appId: string;
  formId: string;
  fields: Array<{ column: ColumnOut; field: FormField }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState<Record<string, string | boolean>>({});
  const [done, setDone] = React.useState<string | null>(null);
  // Per-file-field upload state (keyed by column name).
  const [uploads, setUploads] = React.useState<
    Record<string, { name?: string; uploading: boolean; error?: string }>
  >({});
  // Full attachment descriptors keyed by column name — a `file`-type column
  // stores the whole descriptor (S2); a legacy text column stores just the ref.
  const [fileDescriptors, setFileDescriptors] = React.useState<
    Record<string, AttachmentOut>
  >({});

  React.useEffect(() => {
    if (open) {
      // Seed from each field's default value.
      const seed: Record<string, string | boolean> = {};
      for (const { column, field } of fields) {
        if (field.default == null) continue;
        seed[column.name] =
          effectiveWidget(column, field) === "boolean"
            ? Boolean(field.default)
            : String(field.default);
      }
      setDraft(seed);
      setUploads({});
      setFileDescriptors({});
      setDone(null);
    }
  }, [open, fields]);

  // Upload a file for an attachment field, then stash its blob ref as the
  // field's value (the row stores the ref, not the bytes).
  const onFile = async (colName: string, file: File | null) => {
    if (!file) return;
    setUploads((u) => ({ ...u, [colName]: { uploading: true } }));
    try {
      const res = await appsApi.uploadAttachment(appId, formId, file);
      setDraft((d) => ({ ...d, [colName]: res.ref }));
      setFileDescriptors((m) => ({ ...m, [colName]: res }));
      setUploads((u) => ({ ...u, [colName]: { uploading: false, name: res.filename } }));
    } catch (e) {
      setUploads((u) => ({ ...u, [colName]: { uploading: false, error: (e as Error).message } }));
    }
  };

  const submit = useMutation({
    mutationFn: (data: Row) => appsApi.submitForm(appId, formId, data),
    onSuccess: (res) => setDone(res.submit_message ?? t("apps.submitted", "Submitted")),
  });

  // Fields visible given the current draft (conditional show-when).
  const visible = fields.filter(({ field }) => isFieldVisible(field.show_when, draft));

  const buildPayload = (): Row => {
    const out: Row = {};
    for (const { column, field } of visible) {
      // A `file`-type column stores the whole attachment descriptor (S2);
      // a file widget over a text column still stores just the ref string.
      if (column.data_type === "file") {
        const desc = fileDescriptors[column.name];
        if (desc) out[column.name] = desc;
        else if (fieldRequired(column, field)) out[column.name] = null;
        continue;
      }
      // Coerce to the column's data type (a textarea is still text, a
      // number widget still feeds a numeric coercion).
      const w = effectiveWidget(column, field);
      // file widget on a text column → the value is the uploaded blob ref.
      // date/datetime/email/url are thin HTML5 inputs → their value is a
      // string the backend (text or a date/timestamptz column) stores as-is.
      const coerceKind =
        w === "textarea" || w === "file" || w === "date" || w === "datetime" || w === "email" || w === "url"
          ? "text"
          : w;
      const v = coerceValue(coerceKind, draft[column.name] ?? "", fieldRequired(column, field));
      if (v !== undefined) out[column.name] = v;
    }
    return out;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.fillForm", "Fill / test")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {done ? (
            <Alert tone="info" title={t("apps.submitted", "Submitted")}>
              <span className="text-sm">{done}</span>
            </Alert>
          ) : (
            <>
              {visible.map(({ column, field }) => {
                const widget = effectiveWidget(column, field);
                const label = field.label || column.name;
                const value = draft[column.name];
                const strValue = typeof value === "boolean" ? "" : (value ?? "");
                return (
                  <div key={column.id} className="space-y-1.5">
                    <Label htmlFor={`f-${column.id}`}>
                      {label}
                      {fieldRequired(column, field) && <span className="ml-1 text-danger">*</span>}
                    </Label>
                    {widget === "boolean" ? (
                      <Switch
                        id={`f-${column.id}`}
                        checked={Boolean(value)}
                        onCheckedChange={(v) => setDraft((d) => ({ ...d, [column.name]: v }))}
                      />
                    ) : widget === "file" ? (
                      <div className="space-y-1">
                        <input
                          id={`f-${column.id}`}
                          type="file"
                          className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-2 file:py-1 file:text-sm"
                          onChange={(e) => onFile(column.name, e.target.files?.[0] ?? null)}
                        />
                        {uploads[column.name]?.uploading && (
                          <p className="text-[11px] text-muted-foreground">
                            {t("apps.uploading", "Uploading…")}
                          </p>
                        )}
                        {uploads[column.name]?.name && !uploads[column.name]?.uploading && (
                          <p className="text-[11px] text-success">{uploads[column.name]?.name}</p>
                        )}
                        {uploads[column.name]?.error && (
                          <p className="text-[11px] text-danger">{uploads[column.name]?.error}</p>
                        )}
                      </div>
                    ) : widget === "select" ? (
                      <select
                        id={`f-${column.id}`}
                        value={strValue}
                        className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                        onChange={(e) => setDraft((d) => ({ ...d, [column.name]: e.target.value }))}
                      >
                        <option value="">—</option>
                        {columnOptions(column).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : widget === "textarea" ? (
                      <textarea
                        id={`f-${column.id}`}
                        value={strValue}
                        rows={3}
                        className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                        onChange={(e) => setDraft((d) => ({ ...d, [column.name]: e.target.value }))}
                      />
                    ) : (
                      <Input
                        id={`f-${column.id}`}
                        type={
                          widget === "number"
                            ? "number"
                            : widget === "datetime"
                              ? "datetime-local"
                              : widget === "date" || widget === "email" || widget === "url"
                                ? widget
                                : "text"
                        }
                        value={
                          widget === "datetime" && typeof strValue === "string"
                            ? strValue.slice(0, 16)
                            : strValue
                        }
                        onChange={(e) => setDraft((d) => ({ ...d, [column.name]: e.target.value }))}
                      />
                    )}
                    {field.help && <p className="text-[11px] text-muted-foreground">{field.help}</p>}
                  </div>
                );
              })}
              {submit.isError && (
                <Alert tone="danger" title={t("apps.submitFailed", "Submit failed")}>
                  <span className="font-mono text-xs">{(submit.error as Error).message}</span>
                </Alert>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {done ? t("common.close", "Close") : t("common.cancel", "Cancel")}
          </Button>
          {!done && (
            <Button disabled={submit.isPending} onClick={() => submit.mutate(buildPayload())}>
              {t("apps.submit", "Submit")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
